<template>
  <div v-hotkey="shortcuts" class="database-page">
    <DatabaseTopBar
      v-if="isMounted"
      :workspace-id="workspaceId"
      :database-id="databaseId"
      :title-scrolled-past="isTitleScrolledPast"
      :database-title="databaseTitle"
      :can-delete="canDeleteDatabase"
      @copy-link="copyLink"
      @delete="deleteDatabase"
    />

    <div
      ref="databaseScreenEl"
      id="scrollable-database"
      class="database-screen"
    >
      <div ref="databaseTitleEl" class="database-title">
        <TypeIcon :type="database" class="title-icon" />
        <span class="name">{{ databaseTitle }}</span>
      </div>

      <View
        ref="viewEl"
        v-model:query="viewQuery"
        v-model:layout="layout"
        :workspace-id="workspaceId"
        :storage-key-prefix="`database/${workspaceId}/${databaseId}`"
        :parameters="{
          configParameters: { datasource: false, delete: false },
        }"
        :gated="false"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";

import { copy } from "@/clipboard";
import { getDatabaseTitle } from "@/components/databases";
import DatabaseTopBar from "@/components/DatabaseTopBar.vue";
import type { DisplayState } from "@/components/graph/component";
import {
  isProtectedBuiltinTable,
  isProvisionedDatabase,
} from "@/components/graph/provisioned";
import * as inject from "@/components/inject";
import {
  assignShortcuts,
  copyLinkItem,
  findInPage,
  type Shortcuts,
} from "@/components/shortcuts";
import TypeIcon from "@/components/TypeIcon.vue";
import { getDefaultViewConfigForDataSource } from "@/components/views/config/default";
import View, { type Query } from "@/components/views/View.vue";
import { getOrganizationIdFromWorkspaceId } from "@/components/workspaces";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { isWorkspaceWritable } from "@/composables/useProfile";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import {
  DELETE_TYPE_CONFIRMATION_COPY,
  requestDeleteConfirmation,
} from "@/utils/confirmation";
import { navigateTo, resolveRouteFullPath } from "@/utils/navigate";

export default defineComponent({
  name: "DatabaseScreen",
  components: {
    DatabaseTopBar,
    TypeIcon,
    View,
  },
  props: {
    workspaceId: { type: String, required: true },
    databaseId: { type: String, required: true },
  },
  async setup(props) {
    const controller = inject.controller();
    const profile = inject.profile();
    const workspaceDatabases = inject.workspaceDatabases();
    const router = inject.router();
    const simpleModal = inject.simpleModal();
    const isModalShown = inject.isModalShown();
    const isInputFocused = inject.isInputFocused();

    const isMounted = ref(false);
    const viewEl = ref<InstanceType<typeof View> | null>(null);
    const databaseScreenEl = ref<HTMLDivElement | null>(null);
    const databaseTitleEl = ref<HTMLDivElement | null>(null);
    const isTitleScrolledPast = ref(false);
    let titleObserver: IntersectionObserver | null = null;

    onMounted(() => {
      isMounted.value = true;
      if (databaseScreenEl.value && databaseTitleEl.value) {
        titleObserver = new IntersectionObserver(
          ([entry]) => {
            isTitleScrolledPast.value = entry ? !entry.isIntersecting : false;
          },
          { root: databaseScreenEl.value, threshold: 0 }
        );
        titleObserver.observe(databaseTitleEl.value);
      }
    });

    onBeforeUnmount(() => {
      titleObserver?.disconnect();
      titleObserver = null;
    });

    inject.currentWorkspaceId(computed(() => props.workspaceId));

    const layoutStorageKey = computed(
      () => `database/${props.workspaceId}/${props.databaseId}:layout`
    );
    const queryStorageKey = computed(
      () => `database/${props.workspaceId}/${props.databaseId}:query`
    );

    const releaser = useReleaser();
    const { syncState } = useBrowserStore(profile.value.user.accountId);
    const layout = releaser.add(
      syncState<DisplayState>(layoutStorageKey.value, "table", {
        preset: false,
      })
    );
    const source = releaser.add(syncState<string>(queryStorageKey.value, ""));

    const database = computed(() => {
      const ws = workspaceDatabases.value?.[props.workspaceId];
      return ws ? ws[props.databaseId] : undefined;
    });

    const databaseTitle = computed(() => getDatabaseTitle(database.value));

    // Same Delete visibility as TypeEdition: user-editable types that are
    // neither the personal workspace's fixed built-in tables (by id) nor the
    // designated Task/Page/Project of the workspace — and never for a
    // `reader` member of the workspace.
    const canDeleteDatabase = computed(() => {
      const db = database.value;
      if (!db || db.permissions.can_update_type === false) return false;
      if (!isWorkspaceWritable(profile.value, props.workspaceId)) return false;
      if (isProtectedBuiltinTable(db)) return false;
      const databases = workspaceDatabases.value?.[props.workspaceId];
      if (!databases) return false;
      return !isProvisionedDatabase(db, databases);
    });

    const defaultQuery = ref<string | undefined>(undefined);

    watch(
      database,
      async (db) => {
        if (!db) return;
        // Apply default view config (including visible fields) when nothing loaded from storage
        const config = await getDefaultViewConfigForDataSource(
          controller,
          props.workspaceId,
          db
        );
        const query = await controller.graph.view.toQuery(config);
        defaultQuery.value = query;
        if (!source.value) {
          source.value = query;
        }
      },
      { immediate: true }
    );

    const viewQuery = computed<Query>({
      get: () => ({
        initial: defaultQuery.value,
        current: source.value,
      }),
      set: (v) => {
        source.value = v.current;
      },
    });

    const deleteDatabase = async () => {
      const confirmed = await requestDeleteConfirmation(
        simpleModal,
        DELETE_TYPE_CONFIRMATION_COPY
      );
      if (!confirmed) return;
      const orgId = getOrganizationIdFromWorkspaceId(
        profile.value,
        props.workspaceId
      );
      if (orgId) {
        await navigateTo(RouteNames.Workspace, router, {
          params: { orgId, workspaceId: props.workspaceId },
        });
      }
      controller.graph.database.remove(props.workspaceId, props.databaseId);
    };

    const copyLink = async () => {
      const path = resolveRouteFullPath(router, {
        name: RouteNames.Database,
        params: {
          workspaceId: props.workspaceId,
          databaseId: props.databaseId,
        },
      });
      const url = `https://open.routine.co?path=${path}`;
      try {
        await copy(url);
        controller.notifications.notify("Link copied to clipboard");
      } catch {
        controller.notifications.notify("Failed to copy link");
      }
    };

    const shortcuts = computed<Shortcuts>(() => ({
      ...assignShortcuts({ isModalShown, isInputFocused }, copyLinkItem, () => {
        void copyLink();
      }),
      // Like in a browser, find stays available while an input is focused
      // (e.g. to re-select the term already typed in the search field).
      ...assignShortcuts(
        { isModalShown, isInputFocused: ref(false) },
        findInPage,
        () => {
          viewEl.value?.focusSearch();
        }
      ),
    }));

    return {
      canDeleteDatabase,
      copyLink,
      deleteDatabase,
      database,
      databaseTitle,
      databaseScreenEl,
      databaseTitleEl,
      isMounted,
      isTitleScrolledPast,
      layout,
      shortcuts,
      viewEl,
      viewQuery,
    };
  },
});
</script>

<style scoped lang="scss">
.database-page {
  height: 100%;
  width: 100%;
}

.database-screen {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 64px;
  box-sizing: border-box;
}

.database-title {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 10px;
  padding: 0 0 48px 0;

  .title-icon {
    width: 20px;
    height: 20px;
    font-size: 20px;
    line-height: 20px;

    :deep(svg) {
      width: 20px;
      height: 20px;
    }
  }

  .name {
    @extend %font-h2;
  }
}
</style>
